import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async";

function About() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: "ease-out-cubic" });
  }, []);

  const bgTheme = "bg-gradient-to-b from-[#fffdf8] via-[#fef6e4] to-[#fff3d9] text-gray-900";
  const cardTheme = "bg-white/70 backdrop-blur-md border border-yellow-300/20";
  const accent = "text-yellow-500";
  const shimmerText = "bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-text";

  return (
    <>
      <Helmet>
        <title>About Bait Alafnan | Luxury Property Maintenance Dubai</title>
        <meta
          name="description"
          content="MA Perfect Fix Technical LLC is a Dubai-based luxury maintenance company delivering premium technical services for villas, apartments, offices, and commercial properties."
        />
        <meta
          name="keywords"
          content="Luxury maintenance Dubai, MA Perfect Fix, villa maintenance Dubai, premium technical services, luxury property maintenance, Dubai maintenance company"
        />

        {/* Open Graph */}
        <meta property="og:title" content="About MA Perfect Fix | Luxury Technical Services Dubai" />
        <meta property="og:description" content="Premium maintenance solutions for luxury residential and commercial properties in Dubai." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://maperfectfix.com/about" />
        <meta property="og:image" content="https://maperfectfix.com/assets/logomaa.png" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "MA Perfect Fix Technical LLC",
            url: "https://maperfectfix.com",
            logo: "https://maperfectfix.com/assets/logomaa.png",
            image: "https://maperfectfix.com/assets/logomaa.png",
            telephone: " +971 50 414 7991",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Dubai",
              addressCountry: "AE",
            },
            areaServed: "Dubai",
            description:
              "Luxury property maintenance and technical services company based in Dubai.",
          })}
        </script>
      </Helmet>

      <section className={`${bgTheme} transition-colors duration-500 py-20 sm:py-24 lg:py-28`}>

        {/* TITLE */}
        <div className="text-center mb-14">
          <h1 data-aos="fade-up" className={`text-3xl sm:text-4xl md:text-5xl font-cormorant tracking-wide`}>
            ABOUT <span className={shimmerText}>US</span>
          </h1>
          <div className={`mx-auto mt-4 w-24 h-[3px] bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-pulse rounded`}></div>
        </div>

        {/* INTRO */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p
            data-aos="fade-up"
            className="text-center mb-4 text-[#0a1f44] text-base sm:text-lg md:text-xl leading-relaxed transition-colors duration-500"
          >
            <span className={`${accent} font-semibold`}>Bait Al Afnan Technical LLC</span>{" "}
            is a Dubai-based luxury technical services company delivering
            precision-crafted maintenance solutions for high-end residential and
            commercial properties.
          </p>
            <p
            data-aos="fade-up"
            className="text-center mb-16 text-[#0a1f44] text-base sm:text-lg md:text-xl leading-relaxed transition-colors duration-500"
          >
            <span className={` font-semibold`}>At Bait Al Afnan,</span>{" "}
            we understand that your home is more than just a building—it’s where comfort, safety, and peace of mind matter most. Our highly experienced maintenance team is dedicated to resolving every technical issue, from the simplest to the most complex, with precision and care.Through regular inspections and prompt rectification, we ensure your residence is always maintained to the highest standards. With the Perfect Fix team by your side, you can relax knowing your home is always in perfect condition.

          </p>

          {/* MISSION & VISION */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-20">
            <div data-aos="fade-up" className={`${cardTheme} rounded-3xl p-8 shadow-xl hover:shadow-yellow-400/40 transition`}>
              <h3 className={`font-cormorant text-2xl mb-4 ${accent} bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-text`}>
                Our Vision
              </h3>
              <p className="leading-relaxed transition-colors  text-[#0a1f44] duration-500">
               To become one of Dubai’s most trusted and respected names in luxury technical services, recognized for engineering excellence, ethical leadership, and long-term client partnerships.Under the direction of experienced leadership with more than two decades of industry expertise, we envision setting new benchmarks in preventive maintenance, service quality, and operational reliability.<br/> Our goal is to continuously elevate industry standards by combining deep technical knowledge, modern methodologies, and responsible business practices.We aspire to build an organization defined by integrity, innovation, and disciplined execution—where every service reflects professionalism, precision, and care. By fostering a culture of continuous improvement and client-centric thinking, we aim to deliver dependable solutions that protect property value, enhance comfort, and create lasting confidence for property owners, investors, and facility managers across the region.

              </p>
            </div>

            <div data-aos="fade-up" className={`${cardTheme} rounded-3xl p-8 shadow-xl hover:shadow-yellow-400/40 transition`}>
              <h3 className={`font-cormorant text-2xl mb-4 ${accent} bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-text`}>
                Our Mission
              </h3>
              <p className="leading-relaxed transition-colors text-[#0a1f44] duration-500">
                To deliver exceptional technical services through disciplined engineering practices, preventive maintenance strategies, and professional execution—ensuring safety, reliability, and long-term asset protection for luxury residential and commercial properties.<br/>Guided by leadership with over 20 years of hands-on experience in luxury property maintenance, we are driven by a deep understanding of complex systems, high-end environments, and the expectations of discerning clients.<br/> Our mission extends beyond routine maintenance. We aim to create lasting value by preserving performance, enhancing efficiency, and protecting the long-term integrity of every property we serve.Through structured workflows, skilled technical teams, and responsible service delivery, we commit to maintaining the highest standards of quality, accountability, and operational excellence. We strive to build long-term partnerships founded on trust, transparency, and consistent performance—ensuring peace of mind for our clients and excellence in every engagement.
              </p>
            </div>
          </div>

          {/* WHY CHOOSE US */}
          <div className="text-center mb-14">
            <h2 data-aos="fade-up" className={`text-3xl sm:text-4xl md:text-5xl font-cormorant`}>
              WHY <span className={shimmerText}>CHOOSE US</span>
            </h2>
            <div className={`mx-auto mt-4 w-24 h-[3px] bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-pulse rounded`}></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {[
              {
                title: "Premium Craftsmanship",
                text: "Exceptional workmanship using premium materials and strict quality control.",
              },
              {
                title: "Elite Professionals",
                text: "Highly trained specialists experienced in luxury property environments.",
              },
              {
                title: "Discreet & Reliable",
                text: "Timely execution, confidentiality, and white-glove service standards.",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-aos="fade-up"
                className={`${cardTheme} rounded-3xl p-8 text-center shadow-xl hover:shadow-yellow-400/40 transition-all duration-300`}
              >
                <h4 className={`font-cormorant text-2xl mb-4 ${accent} bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 animate-text`}>
                  {item.title}
                </h4>
                <p className="leading-relaxed transition-colors text-[#0a1f44] duration-500">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default About;
